// Advisory guard for order states picked outside the licensed states on file.
//
// WHY ADVISORY AND NOT A BLOCK: buying outside your licenses is a supported
// action — LeadAssigner (app/lib/lead_assigner.rb) widens the agent's
// licensed_states to include an order's states at assignment time, i.e. the
// system deliberately back-fills the profile rather than refusing the order.
// The failure mode this module addresses is the *accident*: a stray chip tap or
// a "Select All" that quietly buys leads in a state the agent cannot write. So
// the job here is to make an unlicensed pick visible and require one
// acknowledgement — never to refuse it.
//
// The one hard rule lives server-side and is unrelated to this file:
// LeadOrderValidations#validate_owner_funded_states_licensed rejects
// OWNER-FUNDED orders whose states fall outside the receiving agent's licenses.
// Self-funded orders have no such validation, which is the gap this covers.
//
// Shared by every surface where states are picked, so the comparison and the
// copy cannot drift between them:
//   - lead_order_form_controller.js      (classic + aged order form, select2)
//   - realtime_order_controller.js       (single-screen realtime page, chips)
//   - aged_lead_availability_controller.js (aged store, one state per cart add)
// The React order panel has its own copy of the comparison in
// app/frontend/react/lib/unlicensedStates.ts — see the note there for why the
// two bundles can't share one module.

import { showConfirmModal } from './custom_confirm'
import { buildGlAlert } from './gl_alert'

const NOTICE_BASE_CLASS = 'gl-alert gl-alert--compact'
// The classes buildGlAlert adds after the tone on a painted notice.
const NOTICE_PAINTED_CLASS = 'gl-alert--compact mt-2'

// The default subject, and ALSO the person signal: callers pass SELF_SUBJECT
// (or omit subject) when the viewer is the order's recipient, and a
// third-party subject ("Jane's profile") otherwise — so second-person copy
// ("you", "your license") keys off this one string rather than a separate
// flag that could disagree with the subject shown beside it.
export const SELF_SUBJECT = 'your profile'
// Where the licensed-states list actually lives, as the agent will see it in
// the UI (Profile tab → Business Information card). Named once so the confirm
// dialog, the empty-profile prompt and any future surface agree.
export const LICENSED_STATES_LOCATION = 'Profile → Business Information → Licensed States'

/** Uppercase, trim, drop blanks, de-dupe. Mirrors LeadOrder#normalize_states. */
export function normalizeStates(list) {
  const values = (Array.isArray(list) ? list : [])
    .map((state) => String(state ?? '').trim().toUpperCase())
    .filter(Boolean)

  return [...new Set(values)]
}

/**
 * Selected states that the licensed list does not cover.
 *
 * Returns [] when the licensed list is empty — with nothing to compare against
 * every pick would flag, and a warning that fires on all input teaches agents
 * to dismiss it unread. That case is handled by the separate "no licensed
 * states on file" prompt instead.
 */
export function unlicensedStates(selected, licensed) {
  const licensedSet = new Set(normalizeStates(licensed))
  if (licensedSet.size === 0) return []

  return normalizeStates(selected).filter((state) => !licensedSet.has(state))
}

// Built with DOM nodes rather than an innerHTML string on purpose: the shared
// escapeHTML() is textContent-based and so does NOT encode double quotes, which
// makes it unsafe for the profile link's href="…". Property assignment bypasses
// the HTML parser entirely (same reasoning as showPromptModal's input wiring).
//
// The alert itself comes from the shared builder (lib/gl_alert.js); the notice
// element IS the alert (its data-* attributes carry the subject and profile URL),
// so the built alert's classes and children are painted onto it. No role: the
// notice was never a live region.
//
// `bodyNodes` is a list of strings (inserted as text) and/or Elements.
function paintNotice(el, { tone, icon, title, bodyNodes }) {
  const bodyEl = document.createElement('div')
  bodyEl.append(...bodyNodes)

  const alert = buildGlAlert({
    tone,
    icon,
    iconWeight: 'regular',
    title,
    titleTag: 'div',
    body: [bodyEl],
    className: NOTICE_PAINTED_CLASS,
    contentClassName: 'small',
    role: null,
  })
  el.className = alert.className
  el.replaceChildren(...alert.childNodes)
}

// The subject as a profile link when we have somewhere to send them, plain text
// otherwise (a buy-for order's subject is another agent's profile, which the
// buyer cannot edit — hence no profileUrl on that path).
function subjectNode(subject, profileUrl) {
  if (!profileUrl) return subject

  const link = document.createElement('a')
  link.href = profileUrl
  link.target = '_blank'
  link.rel = 'noopener'
  link.textContent = subject

  return link
}

/**
 * Paint (or clear) the inline advisory under a state picker.
 *
 * @param {HTMLElement|null} el - notice container; hidden via `d-none` when there is nothing to say
 * @param {Object} options
 * @param {string[]} options.selected - currently selected state abbreviations
 * @param {string[]} options.licensed - licensed states on file for the order's recipient
 * @param {string} [options.subject=SELF_SUBJECT] - whose profile is being described
 *   (buying-for orders deliver to another agent, so the copy must not say "your";
 *   any subject other than SELF_SUBJECT also switches the money copy to third person)
 * @param {string} [options.profileUrl] - link target for the empty-profile prompt
 * @returns {string[]} the unlicensed states it warned about (empty if none)
 *
 * NOTE: this OWNS the element's className (it has to swap the gl-alert variant
 * and the d-none), so don't hang extra utility classes on the container in HAML
 * — they will be dropped on the first repaint. Extra `data-*` attributes are
 * preserved and are how the subject/profile URL reach this function.
 */
export function renderUnlicensedStatesNotice(el, options = {}) {
  const { selected, licensed, subject = SELF_SUBJECT, profileUrl = null } = options
  if (!el) return []

  const licensedList = normalizeStates(licensed)
  const offenders = unlicensedStates(selected, licensedList)

  // Nothing to compare against: nudge toward filling the profile in so the
  // check can work next time. Non-blocking, and it never gates the submit.
  if (licensedList.length === 0) {
    paintNotice(el, {
      tone: 'info',
      icon: 'ph-info',
      title: 'No licensed states on file',
      bodyNodes: [
        "We can't flag a state picked by accident until ",
        subjectNode(subject, profileUrl),
        ' lists licensed states.',
      ],
    })
    return []
  }

  if (offenders.length === 0) {
    el.className = `${NOTICE_BASE_CLASS} d-none`
    el.replaceChildren()
    return []
  }

  const one = offenders.length === 1

  paintNotice(el, {
    tone: 'warning',
    icon: 'ph-warning',
    // "on ${subject}" reads correctly for both 'your profile' and
    // "Jane's profile"; a possessive would give "Jane's profile's".
    title: `Not in the licensed states on ${subject}: ${offenders.join(', ')}`,
    // "still cost … money" rather than "get bought and billed": three of the
    // four surfaces this notice appears on add to a CART, where nothing is
    // bought or billed until checkout. The cost is true either way; the moment
    // is not, and a warning that overstates is one an agent learns to discount.
    bodyNodes: [
      `Double-check ${one ? 'it was' : 'they were'} selected on purpose — leads in `
        + `${one ? 'this state' : 'these states'} still cost `
        + `${subject === SELF_SUBJECT ? 'you' : 'the agent'} money.`,
    ],
  })

  return offenders
}

/**
 * Ask the buyer to confirm an unlicensed selection before the order goes through.
 *
 * @param {string[]} offenders - unlicensed states, as returned by unlicensedStates()
 * @param {Object} options
 * @param {string[]} options.licensed - licensed states on file
 * @param {string} [options.subject=SELF_SUBJECT] - whose profile is being described
 *   (any subject other than SELF_SUBJECT switches the title and money copy to third person)
 * @param {string} [options.actionLabel='place this order'] - what the buyer is about
 *   to do, phrased to match the BUTTON they pressed ('add it to the cart',
 *   'save this order'). A form with more than one submit must derive this from
 *   the submitter, not hardcode it, or the prompt describes the wrong action.
 * @param {string} [options.profileUrl] - where the buyer can edit their own licensed
 *   states. Turns the fix-it line into a link; without it (a buy-for order, whose
 *   subject is someone else's profile) the line names the location in plain text.
 * @returns {Promise<boolean>} true to proceed, false to go back and fix
 */
export function confirmUnlicensedStates(offenders, options = {}) {
  const { licensed, subject = SELF_SUBJECT, actionLabel = 'place this order', profileUrl = null } = options
  const licensedList = normalizeStates(licensed)
  const one = offenders.length === 1
  const self = subject === SELF_SUBJECT

  // The message says what is wrong; `detail` says how to fix it. An agent whose
  // profile is stale (licensed in 30 states, 8 on file) otherwise sees this
  // dialog on every order with no idea where the list lives — the DM this copy
  // exists to prevent is "how do I select my states?".
  const fix = self
    ? {
      detail: {
        before: `Licensed in ${one ? 'that state' : 'those states'}? Update the list under `,
        after: ' and this warning goes away.',
      },
      linkText: LICENSED_STATES_LOCATION,
      linkHref: profileUrl,
    }
    : {
      detail: {
        before: `If the agent is licensed there, they can update ${LICENSED_STATES_LOCATION} on their profile.`,
      },
    }

  return showConfirmModal(
    `${offenders.join(', ')} ${one ? 'is' : 'are'} not in the licensed states on ${subject} `
      + `(${licensedList.join(', ')}). `
      + `Leads in ${one ? 'that state' : 'those states'} still cost ${self ? 'you' : 'the agent'} money — `
      + `${actionLabel} anyway?`,
    {
      variant: 'warning',
      title: self ? 'States outside your license' : "States outside the agent's license",
      icon: 'ph ph-warning',
      confirmText: 'Yes, I meant to',
      cancelText: 'Go back and fix',
      ...fix,
    },
  )
}

/**
 * The full submit-time gate: resolve the unlicensed picks and, if there are
 * any, require one acknowledgement. Resolves true when there is nothing to
 * warn about, so callers can `if (!(await guard(...))) return` unconditionally.
 */
export async function confirmStatesIfUnlicensed(selected, options = {}) {
  const offenders = unlicensedStates(selected, options.licensed)
  if (offenders.length === 0) return true

  return confirmUnlicensedStates(offenders, options)
}
